import { Component } from '@theme/component';
import { NewsletterSignupEvent } from '@theme/events';

/**
 * Newsletter signup ({% form 'customer' %}). Fires GTM events when Liquid renders
 * success  markup on form.posted_successfully
 *
 * Email is stored on submit because Shopify clears the input after a successful POST.
 *
 * @typedef {Object} EmailSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 */

/**
 * @extends {Component<EmailSignupRefs>}
 */
class EmailSignupComponent extends Component {
  requiredRefs = ['form', 'emailInput'];

  /** @type {(() => void) | null} */
  #submitHandler = null;

  connectedCallback() {
    super.connectedCallback();

    this.#submitHandler = () => this.#storePendingEmail();
    this.refs.form.addEventListener('submit', this.#submitHandler);

    queueMicrotask(() => {
      void this.#handleServerState();
    });
  }

  disconnectedCallback() {
    if (this.#submitHandler) {
      this.refs.form?.removeEventListener('submit', this.#submitHandler);
    }
    super.disconnectedCallback();
  }

  #storePendingEmail() {
    const formId = this.#getFormId();
    const email = this.refs.emailInput.value.trim().toLowerCase();
    if (!formId || !email) return;

    sessionStorage.setItem(this.#pendingEmailKey(formId), email);
  }

  async #handleServerState() {
    const formId = this.#getFormId();
    if (!formId) return;

    const successEl = this.querySelector('[id^="Email-signup__message-success"]');

    if (successEl) {
      const emailHash = await this.#hashEmail(this.#getPostedEmail(successEl, formId));
      sessionStorage.removeItem(this.#pendingEmailKey(formId));
      if (!emailHash) return;
      this.dispatchEvent(new NewsletterSignupEvent(formId, emailHash));
    }
  }

  /**
   * @param {Element} stateEl
   * @param {string} formId
   * @returns {string}
   */
  #getPostedEmail(stateEl, formId) {
    const fromInput = this.refs.emailInput.value.trim();
    if (fromInput) return fromInput;

    const fromPending = sessionStorage.getItem(this.#pendingEmailKey(formId))?.trim();
    if (fromPending) return fromPending;

    const fromDataset = stateEl.dataset.newsletterEmail?.trim();
    if (fromDataset) return fromDataset;

    return '';
  }

  /**
   * @param {string} formId
   * @returns {string}
   */
  #pendingEmailKey(formId) {
    const suffix = formId.includes('__') ? formId.split('__').pop() : formId;
    return `theme:newsletter_pending_email:${suffix}`;
  }

  /**
   * @returns {string}
   */
  #getFormId() {
    if (typeof this.dataset.formId === 'string' && this.dataset.formId) {
      return this.dataset.formId;
    }

    const hidden = this.querySelector('input[name="contact[id]"]');
    if (hidden instanceof HTMLInputElement && hidden.value) {
      return hidden.value;
    }

    return this.refs.form.id || '';
  }

  /**
   * @param {string} email
   * @returns {Promise<string>}
   */
  async #hashEmail(email) {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return '';

    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
}

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignupComponent);
}
